<template>
  <component
    :is="as"
    class="card"
    :class="[
      {
        shadow: props.shadow === true,
        'shadow-on-hover': props.shadow === 'on-hover',
        gap: !!$slots.header && !!$slots.default,
      },
      props.variant,
      props.size,
      heightClass,
    ]"
  >
    <div class="card-header" v-if="$slots.header">
      <slot name="header" />
    </div>

    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { CardProps } from './card-props.interface';

const props = withDefaults(defineProps<CardProps>(), {
  as: 'div',
  variant: 'primary',
  size: 'large',
  height: 'auto',
  shadow: false,
});

const heightClass = computed(() => `height-${props.height}`);
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.card {
  padding: var(--t-spacing-lg);
  transition: all 250ms;
  border-radius: var(--t-border-radius-lg);

  &.primary {
    background-color: var(--t-color-neutral-white);
  }

  &.secondary {
    background-color: var(--t-color-neutral-white);
    box-shadow: var(--t-shadow-sm);
  }

  &.tertiary {
    background-color: var(--t-color-neutral-50);
  }

  &.outline {
    background-color: var(--t-color-neutral-white);
    border: 1px solid var(--t-color-border-secondary);
  }

  &.outline-secondary {
    background-color: var(--t-color-neutral-50);
    border: 1px solid var(--t-color-border-secondary);
  }

  &.small {
    padding: 0.75rem;
  }

  &.medium {
    padding: var(--t-spacing-md);
  }

  &.large {
    padding: var(--t-spacing-lg);
  }

  &.extra-large {
    padding: 2.5rem;
  }

  &.large,
  &.extra-large {
    @include mobile {
      padding: var(--t-spacing-md);
    }
  }

  &.gap {
    & > .card-header {
      margin-bottom: var(--t-spacing-md);
    }

    &.medium > .card-header {
      margin-bottom: var(--t-spacing-sm);
    }
  }

  &.shadow {
    box-shadow: var(--t-shadow-sm);

    &.large,
    &.extra-large {
      box-shadow: var(--t-shadow-md);
    }
  }

  &.shadow-on-hover:hover {
    box-shadow: var(--t-shadow-md);
  }

  &.height-full {
    min-height: 100%;
  }

  &.height-fit-content {
    height: fit-content;
  }

  @include mobile {
    .action {
      opacity: 1;
    }
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
}
</style>
